import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import Select, { ValueType } from 'react-select';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import Button, {
  ButtonSize,
  ButtonType
} from '../../shared/components/core-ui/button/button.component';
import { InputText } from '../../shared/components/core-ui/input-text/input-text.component';
import { Pagination } from '../../shared/components/core-ui/pagination/pagination.component';
import Modal from '../../shared/components/core-ui/modal/modal.component';
import { birdToast } from '../../shared/components/core-ui/toast';
import { canDisableStaffAccount } from '../../shared/config/staff-roles.config';
import { UserRole } from '../../shared/enums/user-role.enum';
import {
  getCurrentUserId,
  hasRole
} from '../../shared/services/helpers/auth-helper/auth-helper.service';
import { isErrorInfo } from '../../shared/interfaces/error-info.interface';
import type { UserModel } from '../../shared/models/user.model';
import {
  disableStaffAccess,
  exportUserAccounts,
  getUserAccounts,
  pageRows
} from './data.service';

interface RoleFilterOption {
  value: string;
  label: string;
}

/**
 * Turn marketingSpecialist into "Marketing Specialist" — the role values are the API's
 * vocabulary, not something to show a human as-is.
 */
function roleLabel(role: string): string {
  return role
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^./, (c) => c.toUpperCase());
}

export interface UserAccountsListProps {
  /** Which roles this list is about. Doubles as the role filter's options. */
  roles: string[];
  title: string;
  /** Where the Create button goes. Omitted means this list has no create. */
  createPath?: string;
  createLabel?: string;
  /** File name stem for the CSV export. */
  exportName: string;
}

const CSV_COLUMNS: { header: string; read: (user: UserModel) => string }[] = [
  { header: 'Name', read: (u) => u.name || '' },
  { header: 'Email', read: (u) => u.email || '' },
  { header: 'Phone', read: (u) => u.phone || '' },
  { header: 'Role', read: (u) => String(u.role || '') },
  { header: 'Roles', read: (u) => (u.roles || []).join(' | ') },
  { header: 'Created', read: (u) => (u.createdAt ? new Date(u.createdAt).toISOString() : '') }
];

/**
 * Quote every field: names and role lists contain commas, and a leading =, +, - or @ is treated
 * as a formula by spreadsheet software.
 */
function toCsvCell(value: string): string {
  const escaped = value.replace(/"/g, '""');
  return /^[=+\-@]/.test(escaped) ? `"'${escaped}"` : `"${escaped}"`;
}

function toCsv(users: UserModel[]): string {
  const header = CSV_COLUMNS.map((c) => toCsvCell(c.header)).join(',');
  const rows = users.map((u) => CSV_COLUMNS.map((c) => toCsvCell(c.read(u))).join(','));
  return [header, ...rows].join('\n');
}

function downloadCsv(csv: string, fileName: string) {
  const link = document.createElement('a');
  link.href = window.URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Listing, search and export for a set of roles, driven by POST /users/search.
 *
 * Kept separate from the Guests list next door, which is a different query entirely
 * (/reservations/distinct-users, filtered by reservation rather than by role).
 */
export const UserAccountsListContainer: React.FC<UserAccountsListProps> = ({
  roles,
  title,
  createPath,
  createLabel,
  exportName
}) => {
  const history = useHistory();

  // Whatever the last request returned: one page when the API paginates, the whole result set
  // when it does not.
  const [rows, setRows] = useState<UserModel[]>([]);
  const [count, setCount] = useState<number>(0);
  const [paginatedByServer, setPaginatedByServer] = useState<boolean>(true);
  // Read inside the page-change effect, which must not re-run when this flips.
  const paginatedByServerRef = useRef<boolean>(true);
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [pendingDisable, setPendingDisable] = useState<UserModel | undefined>(undefined);
  const [isDisabling, setIsDisabling] = useState<boolean>(false);

  const currentUserId = useMemo(() => getCurrentUserId(), []);
  const currentUserIsSuperAdmin = useMemo(() => hasRole([UserRole.SUPER_ADMIN]), []);

  // The applied filters, kept separate from the inputs so typing does not refetch.
  const [applied, setApplied] = useState<{
    name: string;
    email: string;
    phone: string;
    role: string;
  }>({ name: '', email: '', phone: '', role: '' });
  const [name, setName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [phone, setPhone] = useState<string>('');
  const [role, setRole] = useState<string>('');

  // The filter offers exactly the roles this list covers, so narrowing can never widen the list
  // beyond what the screen is for. An empty value means all of them.
  const roleOptions: RoleFilterOption[] = useMemo(
    () => [
      { value: '', label: 'All roles' },
      ...roles.map((r) => ({ value: r, label: roleLabel(r) }))
    ],
    [roles]
  );

  const load = useCallback(
    (pageNumber: number, pageSize: number, filters: typeof applied) => {
      setIsLoading(true);
      const { role: selectedRole, ...text } = filters;
      const queryRoles = selectedRole ? [selectedRole] : roles;
      getUserAccounts({ roles: queryRoles, ...text }, pageNumber, pageSize)
        .then((result) => {
          if (isErrorInfo(result)) {
            birdToast.error('Could not load the list. Please try again.', title);
            return;
          }
          setRows(result.users);
          setCount(result.count);
          setPaginatedByServer(result.paginatedByServer);
          paginatedByServerRef.current = result.paginatedByServer;
        })
        .finally(() => setIsLoading(false));
    },
    // roles is a module-level constant array per route, so this is stable in practice.
    [roles, title]
  );

  const lastQueryKey = useRef<string>('');

  useEffect(() => {
    const queryKey = JSON.stringify([limit, applied]);
    const queryChanged = queryKey !== lastQueryKey.current;
    lastQueryKey.current = queryKey;

    // A page flip needs a request only when the server is doing the paging; otherwise `rows`
    // already holds every match and we just slice it. One effect rather than two, so changing
    // the page size (which also resets the page) cannot fire two identical requests.
    if (queryChanged || paginatedByServerRef.current) {
      load(page, limit, applied);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, applied]);

  const visibleUsers: UserModel[] = pageRows(rows, page, limit, paginatedByServer);

  const handleSearch = () => {
    setPage(1);
    setApplied({ name, email, phone, role });
  };

  const handleClear = () => {
    setName('');
    setEmail('');
    setPhone('');
    setRole('');
    setPage(1);
    setApplied({ name: '', email: '', phone: '', role: '' });
  };

  const handleConfirmDisable = () => {
    if (!pendingDisable?.id) {
      return;
    }
    const target = pendingDisable;
    setIsDisabling(true);
    disableStaffAccess(target.id as string)
      .then((result) => {
        if (isErrorInfo(result)) {
          birdToast.error(
            result.status === 403
              ? 'You do not have permission to change this account.'
              : 'Could not disable this account. Please try again.',
            'Disable staff access'
          );
          return;
        }
        birdToast.success(
          `${target.name || target.email} is now a guest account and no longer has staff access.`,
          'Access disabled'
        );
        setPendingDisable(undefined);
        // The account no longer matches this list's role filter, so refetch rather than patch.
        load(page, limit, applied);
      })
      .finally(() => setIsDisabling(false));
  };

  const handleExport = () => {
    setIsExporting(true);
    exportUserAccounts({
      roles: applied.role ? [applied.role] : roles,
      name: applied.name,
      email: applied.email,
      phone: applied.phone
    })
      .then((result) => {
        if (isErrorInfo(result)) {
          birdToast.error('Could not export the list. Please try again.', title);
          return;
        }
        if (result.length === 0) {
          birdToast.info('Nothing to export for the current filters.', title);
          return;
        }
        downloadCsv(toCsv(result), `${exportName}-${new Date().toISOString().slice(0, 10)}.csv`);
      })
      .finally(() => setIsExporting(false));
  };

  return (
    <div className="p-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-3">
        <h1 className="text-2xl font-gilroy-bold">{title}</h1>
        <div className="flex gap-3">
          <Button
            type={ButtonType.gray}
            size={ButtonSize.medium}
            onClick={handleExport}
            disabled={isExporting || isLoading}
            loading={isExporting}
          >
            Export CSV
          </Button>
          {createPath && (
            <Button
              type={ButtonType.primary}
              size={ButtonSize.medium}
              onClick={() => history.push(createPath)}
            >
              {createLabel || 'Create user'}
            </Button>
          )}
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-3 mb-4">
        <div className="flex-1">
          <InputText
            data={{ value: name, placeholder: 'Name' }}
            config={{ disabled: isLoading }}
            eventHandlers={{
              onChangeHandler: (value) => setName(value),
              onEnterPressedHandler: handleSearch
            }}
          />
        </div>
        <div className="flex-1">
          <InputText
            data={{ value: email, placeholder: 'Email (exact)' }}
            config={{ disabled: isLoading }}
            eventHandlers={{
              onChangeHandler: (value) => setEmail(value),
              onEnterPressedHandler: handleSearch
            }}
          />
        </div>
        <div className="flex-1">
          <InputText
            data={{ value: phone, placeholder: 'Phone (exact)' }}
            config={{ disabled: isLoading }}
            eventHandlers={{
              onChangeHandler: (value) => setPhone(value),
              onEnterPressedHandler: handleSearch
            }}
          />
        </div>
        <div className="flex-1 min-w-[12rem]">
          <Select
            options={roleOptions}
            value={roleOptions.find((option) => option.value === role)}
            isDisabled={isLoading}
            placeholder="All roles"
            menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
            onChange={(option: ValueType<RoleFilterOption, false>) =>
              setRole(option ? option.value : '')
            }
          />
        </div>
        <Button type={ButtonType.primary} size={ButtonSize.medium} onClick={handleSearch}>
          Search
        </Button>
        <Button type={ButtonType.gray} size={ButtonSize.medium} onClick={handleClear}>
          Clear
        </Button>
      </div>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleUsers.length === 0 && !isLoading && (
              <TableRow>
                <TableCell colSpan={6}>No users match these filters.</TableCell>
              </TableRow>
            )}
            {visibleUsers.map((user) => (
              <TableRow key={user.id || user.email}>
                <TableCell>{user.name}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>{user.phone}</TableCell>
                <TableCell>{user.role}</TableCell>
                <TableCell>
                  {user.createdAt ? new Date(user.createdAt).toDateString() : ''}
                </TableCell>
                <TableCell align="right">
                  {(() => {
                    const eligibility = canDisableStaffAccount({
                      targetId: user.id,
                      targetRole: user.role,
                      currentUserId,
                      currentUserIsSuperAdmin
                    });
                    return (
                      <span title={eligibility.reason || 'Move this account to guest'}>
                        <Button
                          type={ButtonType.danger}
                          size={ButtonSize.small}
                          disabled={!eligibility.allowed || isDisabling}
                          onClick={() => setPendingDisable(user)}
                        >
                          Disable
                        </Button>
                      </span>
                    );
                  })()}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Modal
        config={{ showModal: !!pendingDisable, fullScreen: false, showClose: !isDisabling }}
        eventHandlers={{
          closeModalHandler: () => {
            if (!isDisabling) {
              setPendingDisable(undefined);
            }
          }
        }}
      >
        <div className="p-6 max-w-lg">
          <h2 className="text-xl font-gilroy-bold mb-3">Disable staff access?</h2>
          <p className="mb-3">
            <strong>{pendingDisable?.name || pendingDisable?.email}</strong> will be moved from{' '}
            <strong>{pendingDisable?.role}</strong> to a normal guest account. They lose access to
            every admin tool screen their role allowed.
          </p>
          <p className="mb-3 text-sm text-slate-600">
            The account, its history and its login are kept — only the role changes. You can
            restore access by creating it again with the right role, or by editing the account in
            User Search.
          </p>
          <p className="mb-6 text-sm text-slate-600">
            Anyone already signed in keeps their current access until their session refreshes, so
            this can take up to three hours to take effect for them.
          </p>
          <div className="flex gap-3 justify-end">
            <Button
              type={ButtonType.gray}
              size={ButtonSize.medium}
              disabled={isDisabling}
              onClick={() => setPendingDisable(undefined)}
            >
              Cancel
            </Button>
            <Button
              type={ButtonType.danger}
              size={ButtonSize.medium}
              disabled={isDisabling}
              loading={isDisabling}
              onClick={handleConfirmDisable}
            >
              Disable access
            </Button>
          </div>
        </div>
      </Modal>

      {count > 0 && (
        <Pagination
          itemCounts={count}
          itemsPerPage={limit}
          offset={page}
          handlePageChange={(value: number) => setPage(value)}
          handleItemsPerPage={(itemsPerPage: number) => {
            setLimit(itemsPerPage);
            setPage(1);
          }}
        />
      )}
    </div>
  );
};
